/*
 Copyright (c) 2015, salesforce.com, inc. All rights reserved.

 Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
 Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
 Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
 Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

 THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */

import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef } from 'react';
import find from 'lodash.find';

import mcLib from '../api/mc-lib';

import * as JourneyBuilderActions from '../actions/journey-templates';
import JourneyTemplates from '../components/journey-templates';

const I18N = 'I18N_';
const TEMPLATES_LIFECYCLE = 'TEMPLATES_LIFECYCLE_';
const TEMPLATES_COMPLEXITY = 'TEMPLATES_COMPLEXITY_';

function localize(value, localizations) {
  const locString = value.replace(I18N, '').toLowerCase();
  const translation = localizations[`homepage.templates.${locString}`];

  return translation || value;
}

function onDeepLink(options) {
  mcLib.logEvent('Template Carousel', options.analyticsName);
  mcLib.appDeeplink(options);
}

function translateStrings(templates = [], localizations) {
  const newTemplates = [];

  templates.forEach((template, i) => {
    const temp = templates[i];
    const newTemplate = {};

    newTemplate.id = template.id;
    newTemplate.name = template.name && localize(template.name, localizations);
    newTemplate.description = template.description && localize(template.description, localizations);
    newTemplate.lifecycle = template.lifecycle && localize(TEMPLATES_LIFECYCLE + template.lifecycle, localizations);
    newTemplate.complexity = template.complexity && localize(TEMPLATES_COMPLEXITY + template.complexity, localizations);

    newTemplate.complexityData = template.complexity && template.complexity.toLowerCase();
    newTemplate.lifecycleData = template.lifecycle.toLowerCase();
    if (temp.tags && temp.tags.length > 0) {
      newTemplate.tagOne = localize(temp.tags[0].name, localizations);
    }

    if (temp.tags && temp.tags.length > 1) {
      newTemplate.tagTwo = localize(temp.tags[1].name, localizations);
    }

    if (temp.tags && temp.tags.length > 2) {
      newTemplate.tagContent = '';

      temp.tags.forEach((tag, j) => {
        let tagName;

        if (j > 1) {
          tagName = localize(tag.name, localizations);

          if (j > 2) {
            newTemplate.tagContent += `, ${tagName}`;
          } else {
            newTemplate.tagContent += tagName;
          }
        }
      });
    }

    newTemplates.push(newTemplate);
  });

  return newTemplates;
}
export function JourneyTemplatesContainer(props) {
  const previousProps = useRef(props);
  const didRetrievePermissions = useRef(false);
  const getAppData = useCallback(
    ({
      hasJourneyBuilderBusinessRule,
      hasJourneyBuilderCreatePermission,
      isJourneyBuilderProvisioned,
      hasLightningHomepageBusinessRule,
    } = props) => {
      if (
        hasJourneyBuilderBusinessRule &&
        hasLightningHomepageBusinessRule &&
        hasJourneyBuilderCreatePermission &&
        isJourneyBuilderProvisioned
      ) {
        props.journeyBuilderActions.getTemplates();
      } else if (!didRetrievePermissions.current) {
        didRetrievePermissions.current = true;
        props.journeyBuilderActions.hasJourneyBuilderCreatePermission();
        props.journeyBuilderActions.hasJourneyBuilderBR();
        props.journeyBuilderActions.hasLightningHomepageBR();
      }
    },
    [props, didRetrievePermissions]
  );
  useEffect(() => {
    getAppData(props);
  }, []);
  useEffect(() => {
    const prevProps = previousProps.current;

    if (
      (props.hasJourneyBuilderCreatePermission && !prevProps.hasJourneyBuilderCreatePermission) ||
      (props.hasJourneyBuilderBusinessRule && !prevProps.hasJourneyBuilderBusinessRule) ||
      (props.isJourneyBuilderProvisioned && !prevProps.isJourneyBuilderProvisioned) ||
      (props.hasLightningHomepageBusinessRule && !prevProps.hasLightningHomepageBusinessRule)
    ) {
      getAppData(props);
    }

    previousProps.current = props;
  }, [props]);

  if (
    props.hasJourneyBuilderBusinessRule &&
    props.hasLightningHomepageBusinessRule &&
    props.hasJourneyBuilderCreatePermission &&
    props.isJourneyBuilderProvisioned
  ) {
    return (
      <JourneyTemplates
        templates={translateStrings(props.templates, props.localizations)}
        localizations={props.localizations}
        onDeepLink={onDeepLink}
      />
    );
  }

  return null;
}

JourneyTemplatesContainer.displayName = 'Journey Templates';

JourneyTemplatesContainer.propTypes = {
  journeyBuilderActions: PropTypes.object,
  hasJourneyBuilderBusinessRule: PropTypes.bool,
  hasLightningHomepageBusinessRule: PropTypes.bool,
  hasJourneyBuilderCreatePermission: PropTypes.bool,
  templates: PropTypes.array,
  localizations: PropTypes.object,
  isJourneyBuilderProvisioned: PropTypes.bool,
};

function mapStateToProps(state) {
  const isJourneyBuilderProvisioned = !!find(state.applications, { name: 'Journey Builder' });

  return {
    hasJourneyBuilderBusinessRule: state.businessRules.hasJourneyBuilderBusinessRule,
    hasLightningHomepageBusinessRule: state.businessRules.hasLightningHomepageBusinessRule,
    hasJourneyBuilderCreatePermission: state.permissions.hasJourneyBuilderCreatePermission,
    templates: state.journeyTemplateEvents || [],
    localizations: state.localization.localizations || {},
    isJourneyBuilderProvisioned,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    journeyBuilderActions: bindActionCreators(JourneyBuilderActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(JourneyTemplatesContainer);
